import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CurriculumType, CurriculumTypeListResponse, SubjectListResponse, SubjectModel, SubjectPayLoad, TeachingTypeModel } from "../../models/subjects/subject.model";

const SKIP_GLOBAL_ERROR_DIALOG_HEADER = 'X-Skip-Global-Error-Dialog';

interface SubjectListParams {
    preview?: boolean;
    academic_term_id?: number | string | null;
    academic_term_name?: string | null;
    academic_term_year?: number | string | null;
    curriculum_type_id?: number | string | null;
    study_mode_id?: number | string | null;
    available_for_offering?:boolean|null;
    datatable?: boolean;
    year?: string | null;
    code?: string | null;
    name?: string | null;
    page?: number;
    limit?: number;
    sort?: string | null;
    direction?: string | null;
}

export interface CurriculumTypeListParams {
    datatable?: boolean;
    name?: string | null;
    page?: number;
    limit?: number;
    sort?: string | null;
    direction?: string | null;
}

@Injectable({
    providedIn: "root",
})
export class SubjectService {
    private apiUrl = "/subjects";
    private curriculumTypesUrl = "/curriculum_types";
    private teachingType = "/teaching_types";


    #http = inject(HttpClient);

    getTeachingType(): Observable<TeachingTypeModel[]> {
        return this.#http.get<unknown>(this.teachingType)
            .pipe(map((response) => this.normalizeTeachingTypeResponse(response)));
    }

    getSubjectsList(params?: SubjectListParams): Observable<SubjectListResponse> {
        return this.#http.get<unknown>(this.apiUrl, {
            params: this.buildListParams(params),
        }).pipe(
            map((res) => this.normalizeSubjectListResponse(res, params))
        );
    }

    getSubjectById(id: number): Observable<SubjectModel> {
        return this.#http.get<SubjectModel>(`${this.apiUrl}/${id}`);
    }

    createSubject(payload: SubjectPayLoad): Observable<SubjectModel> {
        return this.#http.post<SubjectModel>(this.apiUrl, payload);
    }

    updateSubject(id: number, payload: SubjectPayLoad): Observable<SubjectModel> {
        return this.#http.put<SubjectModel>(`${this.apiUrl}/${id}`, payload);
    }

    deleteSubject(id: number): Observable<void> {
        return this.#http.delete<void>(`${this.apiUrl}/${id}`);
    }

    exportTemplate(): Observable<Blob> {
        return this.#http.get(`${this.apiUrl}/export_template`, {
            responseType: "blob",
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }

    importSubjectsTemplate(file: File): Observable<any> {
        const formData = new FormData();

        formData.append('file', file);

        return this.#http.post(`${this.apiUrl}/import`, formData, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }

    getCurriculumTypes(params?: CurriculumTypeListParams): Observable<CurriculumType[]> {
        return this.#http.get<unknown>(this.curriculumTypesUrl, {
            params: this.buildCurriculumTypeListParams(params),
        }).pipe(
            map((response) => this.normalizeCurriculumTypesResponse(response))
        );
    }

    getCurriculumTypesList(params?: CurriculumTypeListParams): Observable<CurriculumTypeListResponse> {
        return this.#http.get<unknown>(this.curriculumTypesUrl, {
            params: this.buildCurriculumTypeListParams(params),
        }).pipe(
            map((response) => this.normalizeCurriculumTypesListResponse(response, params))
        );
    }

    private buildCurriculumTypeListParams(params?: CurriculumTypeListParams): HttpParams {
        let httpParams = new HttpParams();

        if (params?.datatable != null) {
            httpParams = httpParams.set('datatable', String(params.datatable));
        }

        if (params?.name != null && params.name !== '') {
            httpParams = httpParams.set('name', params.name);
        }

        if (params?.page != null) {
            httpParams = httpParams.set('page', String(params.page));
        }

        if (params?.limit != null) {
            httpParams = httpParams.set('limit', String(params.limit));
        }

        if (params?.sort != null && params.sort !== '') {
            httpParams = httpParams.set('sort', params.sort);
        }

        if (params?.direction != null && params.direction !== '') {
            httpParams = httpParams.set('direction', params.direction);
        }

        return httpParams;
    }

    private buildListParams(params?: SubjectListParams): HttpParams {
        let httpParams = new HttpParams();

        const paramKeys: (keyof SubjectListParams)[] = [
            'academic_term_id',
            'preview',
            'academic_term_name',
            'academic_term_year',
            'curriculum_type_id',
            'available_for_offering',
            'datatable',
            'year',
            'code',
            'name',
            'page',
            'limit',
            'sort',
            'direction',
        ];

        paramKeys.forEach((key) => {
            httpParams = this.setParamIfPresent(httpParams, key, params?.[key]);
        });

        if (params && 'study_mode_id' in params) {
            const studyModeId = params.study_mode_id == null ? 'null' : String(params.study_mode_id);
            httpParams = httpParams.set('study_mode_id', studyModeId);
        }

        return httpParams;
    }

    private setParamIfPresent(
        httpParams: HttpParams,
        key: string,
        value: string | number | boolean | null | undefined
    ): HttpParams {
        if (value == null || value === '') {
            return httpParams;
        }

        return httpParams.set(key, String(value));
    }

    private normalizeSubjectListResponse(
        res: unknown,
        params?: SubjectListParams
    ): SubjectListResponse {
        if (res !== null && typeof res === "object" && !Array.isArray(res)) {
            const response = res as Record<string, unknown>;

            if (Array.isArray(response.data)) {
                return {
                    data: response.data as SubjectModel[],
                    page: Number(response.page ?? params?.page ?? 1),
                    limit: Number(response.limit ?? params?.limit ?? 10),
                    total: Number(response.total ?? response.data.length),
                    total_pages: Number(response.total_pages ?? 1),
                };
            }
        }

        const data = Array.isArray(res) ? res as SubjectModel[] : [];
        const limit = params?.limit ?? 10;

        return {
            data,
            page: params?.page ?? 1,
            limit,
            total: data.length,
            total_pages: Math.max(1, Math.ceil(data.length / limit)),
        };
    }

    private normalizeTeachingTypeResponse(response: unknown): TeachingTypeModel[] {
        if (Array.isArray(response)) {
            return response as TeachingTypeModel[];
        }

        if (
            typeof response === 'object'
            && response !== null
            && Array.isArray((response as { data?: unknown }).data)
        ) {
            return (response as { data: TeachingTypeModel[] }).data;
        }

        return [];
    }

    private normalizeCurriculumTypesResponse(response: unknown): CurriculumType[] {
        if (Array.isArray(response)) {
            return response as CurriculumType[];
        }

        if (
            typeof response === 'object'
            && response !== null
            && Array.isArray((response as { data?: unknown }).data)
        ) {
            return (response as { data: CurriculumType[] }).data;
        }

        return [];
    }

    private normalizeCurriculumTypesListResponse(
        response: unknown,
        params?: CurriculumTypeListParams,
    ): CurriculumTypeListResponse {
        if (response !== null && typeof response === "object" && !Array.isArray(response)) {
            const curriculumTypeResponse = response as Record<string, unknown>;

            if (Array.isArray(curriculumTypeResponse.data)) {
                return {
                    data: curriculumTypeResponse.data as CurriculumType[],
                    page: Number(curriculumTypeResponse.page ?? params?.page ?? 1),
                    limit: Number(curriculumTypeResponse.limit ?? params?.limit ?? 10),
                    total: Number(curriculumTypeResponse.total ?? curriculumTypeResponse.data.length),
                    total_pages: Number(curriculumTypeResponse.total_pages ?? 1),
                };
            }
        }

        const data = Array.isArray(response) ? response as CurriculumType[] : [];
        const limit = params?.limit ?? 10;

        return {
            data,
            page: params?.page ?? 1,
            limit,
            total: data.length,
            total_pages: Math.max(1, Math.ceil(data.length / limit)),
        };
    }
}
